import { useCallback, useEffect, useState } from "react";
import { getEntityTags } from "../Api/TagsApi";

/**
 * The operational status tags for one entity.
 *
 * This is the only source for operational status on the Digital Twin: the real-time
 * info, real-time matrix and digital-twin tags endpoints carry none of it. The header
 * was already making this exact request inside OperationalStatusAction; lifting it
 * here lets the corner marker and the status pill share one call instead of two.
 *
 * `version` is the header's operationalStatusVersion - bumping it after an in-place
 * update re-reads the tags so the marker refreshes without a reload.
 *
 * Returns the raw tag list too, so a consumer that needs the full set (the status
 * modal) can reuse it rather than fetching again.
 */
export const useEntityOperationalStatus = (entityID, { entityType = "battery", version = 0, enabled = true } = {}) => {
    const [tags, setTags] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchTags = useCallback(async () => {
        if (!enabled || !entityID) return undefined;
        setIsLoading(true);
        try {
            const res = await getEntityTags(entityType, entityID);
            return res?.response?.data?.tags || [];
        } catch (err) {
            console.error("Failed to load operational status tags", err);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [entityID, entityType, enabled]);

    useEffect(() => {
        // Guard against an out-of-order response: if entityID changes mid-flight the
        // older request can land last and overwrite the newer entity's tags.
        let ignore = false;
        (async () => {
            const result = await fetchTags();
            if (!ignore && result !== undefined) setTags(result);
        })();
        return () => { ignore = true; };
    }, [fetchTags, version]);

    const statusTag = (tags || []).find((tag) => tag?.tagCode === "status");
    const reasonTag = (tags || []).find((tag) => tag?.tagCode === "sub_status");

    return {
        tags,
        isLoading,
        // valueCode is the stable identifier; the marker's rules match on it.
        status: statusTag?.valueCode,
        statusLabel: statusTag?.valueDisplayname,
        // Reason is shown to the user, so it is the display name, not the code.
        reason: reasonTag?.valueDisplayname,
        refetch: async () => {
            const result = await fetchTags();
            if (result !== undefined) setTags(result);
        },
    };
};

export default useEntityOperationalStatus;
